import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import {
  useDomainStore,
  useFamilyStore,
  usePersonFactsStore,
  usePersonLifeEventsStore,
  usePersonStoriesStore,
  useSuggestionsStore
} from '@/hooks/useStores';
import { mediaApi } from '@/api/mediaWrapper';
import PersonEditForm from '@/components/person/PersonEditForm';
import PersonFacts from '@/components/person/PersonFacts';
import PersonLifeEvents from '@/components/person/PersonLifeEvents';
import PersonRelationships from '@/components/person/PersonRelationships';
import PersonStories from '@/components/person/PersonStories';
import ProfilePhotoUpload from '@/components/person/ProfilePhotoUpload';
import RoleExplanationModal from '@/components/modals/RoleExplanationModal';
import ManageMemberAccessModal from '@/components/modals/ManageMemberAccessModal';
import SuggestChangesModal from '@/components/suggestions/SuggestChangesModal';
import ProfilePendingSuggestionsBanner from '@/components/suggestions/ProfilePendingSuggestionsBanner';
import UpgradeFamilyPlanModal from '@/components/family/UpgradeFamilyPlanModal';
import type { PersonResponse } from 'we-tree-api-client/v2';
import { formatTemporal, calendarYearFromStoredDate } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import { peopleApi } from '@/api/peopleWrapper';
import {
  canInvite,
  getMemberAccessPrefix,
  getRoleDisplayName,
  memberAccessFromRoleExplanation,
  type MemberAccessState
} from '@/utils/rolePermissions';
import { Lock, Unlock } from '@/icons';
import Target from '@/icons/Target';
import Pencil from '@/icons/Pencil';
import ProfileMoreMenu from '@/components/person/ProfileMoreMenu';
import StickyTabStrip from '@/components/shared/StickyTabStrip';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import PageTourIntro from '@/components/help/PageTourIntro';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import { PERSON_PROFILE_TOUR_STEPS, PERSON_PROFILE_TOUR_STORAGE_KEY } from '@/data/person-profile-tour';
import type { ProfileLocationState } from '@/types/profile-navigation';
import type { ProfileSectionTab } from '@/types/profile-sections';
import { PROFILE_SECTION_LEDE_KEYS, parseProfileSectionParam } from '@/types/profile-sections';

import './person-profile-screen.css';

function profileInitials(givenName?: string | null, surname?: string | null): string {
  const a = givenName?.trim()?.[0] ?? '';
  const b = surname?.trim()?.[0] ?? '';
  const initials = `${a}${b}`.toUpperCase();
  return initials || '?';
}

const PersonProfileScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const personFactsStore = usePersonFactsStore();
  const personLifeEventsStore = usePersonLifeEventsStore();
  const personStoriesStore = usePersonStoriesStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam, familyMemberId: familyMemberIdParam } = useParams<{
    familyId: string;
    familyMemberId: string;
  }>();

  // Get the source view from location state (if available)
  const locationState = location.state as ProfileLocationState | undefined;

  // If `location.state` is present, persist it as a fallback for refreshes/new tabs.
  useEffect(() => {
    if (!familyIdParam || !familyMemberIdParam) return;
    if (!locationState?.from) return;

    try {
      sessionStorage.setItem(
        `profileBackFrom_${familyIdParam}`,
        JSON.stringify({
          screen: 'people',
          familyMemberId: familyMemberIdParam,
          from: locationState.from,
          originalAnchorId: locationState.originalAnchorId
        })
      );
    } catch {
      // Ignore storage errors (private mode, etc.)
    }
  }, [familyIdParam, familyMemberIdParam, locationState]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [personData, setPersonData] = useState<PersonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [memberAccess, setMemberAccess] = useState<MemberAccessState>({
    effectiveRole: null,
    accessMode: 'view',
    canDirectModify: false,
    canSuggest: false,
    canReview: false,
    canManageAccess: false
  });
  const [showRoleExplanation, setShowRoleExplanation] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoDisplayStatus, setPhotoDisplayStatus] = useState<'safe' | 'processing' | 'unavailable' | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<ProfileSectionTab>('facts');
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [suggestSubmittedMessage, setSuggestSubmittedMessage] = useState<string | null>(null);
  const suggestionsStore = useSuggestionsStore();

  const profileTour = useScreenTour({
    steps: PERSON_PROFILE_TOUR_STEPS,
    completedStorageKey: PERSON_PROFILE_TOUR_STORAGE_KEY
  });

  // Check URL for edit mode on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldBeInEditMode = searchParams.get('edit') === 'true';
    // Only allow edit mode if user has permission
    if (shouldBeInEditMode && !memberAccess.canDirectModify) {
      // Remove edit mode from URL if user doesn't have permission
      navigate(location.pathname, { replace: true, state: locationState });
      setIsEditMode(false);
    } else {
      setIsEditMode(shouldBeInEditMode);
    }
  }, [location.search, memberAccess.canDirectModify, navigate, location.pathname, locationState]);

  useEffect(() => {
    const section = parseProfileSectionParam(new URLSearchParams(location.search).get('section'));
    if (section) {
      setActiveSection(section);
    }
  }, [location.search]);

  useEffect(() => {
    const section = parseProfileSectionParam(new URLSearchParams(location.search).get('section'));
    if (section !== 'relationships' || isLoading) return;

    const frame = requestAnimationFrame(() => {
      document.getElementById('profile-sections')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => cancelAnimationFrame(frame);
  }, [location.search, isLoading]);

  useEffect(() => {
    const loadPersonData = async () => {
      if (!familyIdParam || !familyMemberIdParam) {
        setErrorMessage(t('personProfile.missingIds'));
        setIsLoading(false);
        return;
      }

      setErrorMessage(null);
      setIsLoading(true);

      try {
        // Set the selected family in the root store
        domainStore.setSelectedFamily(familyIdParam);

        // Load family data if not already loaded
        if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
          await familyStore.loadFamilyData(familyIdParam);
        }

        // Fetch full person data from store (which calls API)
        const result = await familyStore.fetchPerson(familyMemberIdParam);
        if (result && result.success) {
          setPersonData(result.data);
          // Check if user can modify this member and get effective role (for Edit vs Suggest label)
          const roleExplanationResult = await peopleApi.getRoleExplanation(familyMemberIdParam);
          if (roleExplanationResult.success && roleExplanationResult.data) {
            setMemberAccess(memberAccessFromRoleExplanation(roleExplanationResult.data));
          } else {
            setMemberAccess(memberAccessFromRoleExplanation(null));
          }
          // Fetch primary profile photo for safe display (URL + processing/unavailable status)
          if (familyIdParam) {
            const photoResult = await mediaApi.getPrimaryProfilePhotoDisplay(
              familyIdParam,
              familyMemberIdParam,
              '200x200'
            );
            if (photoResult.success && photoResult.data) {
              setPhotoUrl(photoResult.data.url);
              setPhotoDisplayStatus(photoResult.data.displayStatus);
            } else {
              setPhotoUrl(null);
              setPhotoDisplayStatus(null);
            }
          }
        } else {
          const error =
            result && result.error instanceof Error ? result.error : new Error(t('personProfile.loadFailed'));
          setErrorMessage(error.message || t('personProfile.loadFailed'));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load person data:', error);
        setErrorMessage(t('personProfile.loadFailedRetry'));
      } finally {
        setIsLoading(false);
      }
    };

    loadPersonData();
  }, [domainStore, familyStore, familyIdParam, familyMemberIdParam, t]);

  useEffect(() => {
    if (!familyMemberIdParam || !familyIdParam) return;
    personFactsStore.loadFactsForFamilyMember(familyMemberIdParam);
    personLifeEventsStore.loadLifeEventsForFamilyMember(familyMemberIdParam);
    personStoriesStore.loadStoriesForFamilyMember(familyIdParam, familyMemberIdParam);
  }, [familyIdParam, familyMemberIdParam, personFactsStore, personLifeEventsStore, personStoriesStore]);

  const factsCount = familyMemberIdParam ? personFactsStore.getFactsForFamilyMember(familyMemberIdParam).length : 0;
  const lifeEventsCount = familyMemberIdParam
    ? personLifeEventsStore.getLifeEventsForFamilyMember(familyMemberIdParam).length
    : 0;
  const relationshipsCount = familyMemberIdParam ? familyStore.getProfileRelationshipCount(familyMemberIdParam) : 0;
  const memoriesCount = familyMemberIdParam
    ? personStoriesStore.getStoriesForFamilyMember(familyMemberIdParam).length
    : 0;

  const handleRefetchPerson = async () => {
    if (!familyMemberIdParam) return;
    const result = await familyStore.fetchPerson(familyMemberIdParam);
    if (result?.success && result.data) setPersonData(result.data);
  };

  const handlePhotoUploadSuccess = async () => {
    await handleRefetchPerson();
    if (familyIdParam && familyMemberIdParam) {
      const photoResult = await mediaApi.getPrimaryProfilePhotoDisplay(familyIdParam, familyMemberIdParam, '200x200');
      if (photoResult.success && photoResult.data) {
        setPhotoUrl(photoResult.data.url);
        setPhotoDisplayStatus(photoResult.data.displayStatus);
      } else {
        setPhotoUrl(null);
        setPhotoDisplayStatus(null);
      }
    }
  };

  const handleEdit = async () => {
    if (!familyMemberIdParam) return;
    const roleResult = await peopleApi.getRoleExplanation(familyMemberIdParam);
    const access = memberAccessFromRoleExplanation(roleResult.success ? roleResult.data : null);
    if (!access.canDirectModify) {
      return;
    }
    setMemberAccess(access);
    setIsEditMode(true);
    navigate(`${location.pathname}?edit=true`, { replace: true, state: locationState });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Remove edit mode from URL
    navigate(location.pathname, { replace: true, state: locationState });
    // Reload person data to discard any changes
    if (familyMemberIdParam) {
      const loadData = async () => {
        const result = await familyStore.fetchPerson(familyMemberIdParam);
        if (result && result.success) {
          setPersonData(result.data);
        }
      };
      loadData();
    }
  };

  const handleSave = async (updatedData: PersonResponse) => {
    setPersonData(updatedData);
    setIsEditMode(false);
    // Remove edit mode from URL
    navigate(location.pathname, { replace: true, state: locationState });
    // Reload family data to refresh stores
    if (familyIdParam) {
      await familyStore.loadFamilyData(familyIdParam, true);
    }
  };

  if (isLoading) {
    return (
      <div className="person-profile-screen">
        <div className="person-profile-screen__loading">
          <div className="person-profile-screen__spinner" />
          <p className="person-profile-screen__loading-text">{t('personProfile.loading')}</p>
        </div>
      </div>
    );
  }

  // Determine back navigation target
  const handleBack = () => {
    if (!familyIdParam || !familyMemberIdParam) return;

    const storageKey = `profileBackFrom_${familyIdParam}`;

    const resolveState = (): ProfileLocationState | undefined => {
      if (locationState?.from) return locationState;

      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as
          | {
              screen?: string;
              familyMemberId?: string;
              from?: ProfileLocationState['from'];
              originalAnchorId?: string;
            }
          | undefined;

        if (parsed?.screen === 'people' && parsed.familyMemberId === familyMemberIdParam && parsed.from) {
          return { from: parsed.from, originalAnchorId: parsed.originalAnchorId };
        }
      } catch {
        // Ignore storage errors/parse issues.
      }

      return undefined;
    };

    const resolved = resolveState();
    const from = resolved?.from;

    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }

    // If we came from the Members list, go back there.
    if (from === 'members') {
      navigate(`/families/${familyIdParam}/members`);
      return;
    }

    // Default: Tree (return to the original anchor if we have it; otherwise the current person).
    const anchorId = from === 'tree' && resolved?.originalAnchorId ? resolved.originalAnchorId : familyMemberIdParam;

    navigate(`/families/${familyIdParam}/hybrid-pedigree/${anchorId}`);
  };

  if (errorMessage || !personData) {
    return (
      <div className="person-profile-screen">
        <div className="person-profile-screen__error">
          <h3 className="person-profile-screen__error-title">{errorMessage || t('personProfile.notFound')}</h3>
          <button onClick={handleBack} className="person-profile-screen__back-button">
            {tCommon('back')}
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="person-profile-screen">
        <PersonEditForm
          personData={personData}
          onSave={handleSave}
          onCancel={handleCancel}
          familyId={familyIdParam!}
          photoUrl={photoUrl}
          photoDisplayStatus={photoDisplayStatus}
          onPhotoUploadSuccess={handlePhotoUploadSuccess}
        />
      </div>
    );
  }

  const givenAndMiddle = [personData.givenName, personData.middleName].filter(Boolean).join(' ') || tCommon('unknown');
  const surname = personData.surname?.trim() ?? '';
  const suffix = personData.suffix?.trim() ?? '';
  const isDeceased = personData.livingStatus === 'deceased';
  const showDeathLines =
    isDeceased || !!personData.deathDate || !!(personData.deathPlace && personData.deathPlace.trim());

  const storePerson = familyMemberIdParam ? familyStore.getPerson(familyMemberIdParam) : undefined;
  const lifespanText = (() => {
    if (storePerson?.age) return storePerson.age;
    const birthYear = calendarYearFromStoredDate(personData.birthDate);
    if (!birthYear) return null;
    if (isDeceased) {
      const deathYear = calendarYearFromStoredDate(personData.deathDate);
      if (deathYear != null) return t('personProfile.yearsSpan', { count: deathYear - birthYear });
      return null;
    }
    return t('personProfile.yearsOld', { count: new Date().getFullYear() - birthYear });
  })();

  const isClaimedByYou = !!personData.userId && !!domainStore.user?.id && personData.userId === domainStore.user.id;

  const preferredName = personData.preferredName?.trim() ?? '';
  const showPreferredChip = !!preferredName && preferredName !== (personData.givenName?.trim() ?? '');

  const handleViewTree = () => {
    if (familyIdParam && familyMemberIdParam) {
      navigate(`/families/${familyIdParam}/hybrid-pedigree/${familyMemberIdParam}`);
    }
  };

  const { effectiveRole, canDirectModify, accessMode, canManageAccess } = memberAccess;
  const canSuggest = accessMode === 'suggest';
  const accessPrefix = getMemberAccessPrefix(accessMode);

  const canDelete =
    !personData.userId &&
    (canDirectModify || canInvite(domainStore.currentFamily?.userRole, domainStore.currentFamily?.plan)) &&
    familyIdParam &&
    familyMemberIdParam;

  const handleDeleteClick = async () => {
    if (!familyMemberIdParam || !familyIdParam || !canDelete) return;
    if (!window.confirm(t('personProfile.confirmDelete'))) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const result = await peopleApi.deletePerson(familyMemberIdParam);
      if (result.success) {
        await familyStore.loadFamilyData(familyIdParam, true);
        navigate(`/families/${familyIdParam}/members`);
      } else {
        setDeleteError(result.error?.message ?? t('personProfile.deleteFailedLinked'));
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('personProfile.deleteFailedRetry'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="person-profile-screen profile-page">
      <main className="profile-page__main">
        <PageTourIntro className="profile-page__tour-intro" onStartTour={profileTour.startTour}>
          <p className="page-intro profile-page__lede" data-tour="profile-intro">
            {t('personProfile.lede')} <ScreenHelpDot />
          </p>
        </PageTourIntro>
        {familyIdParam && familyMemberIdParam ? (
          <ProfilePendingSuggestionsBanner familyId={familyIdParam} familyMemberId={familyMemberIdParam} />
        ) : null}
        {suggestSubmittedMessage ? (
          <p className="profile-suggest-confirmation" role="status">
            {suggestSubmittedMessage}
          </p>
        ) : null}
        {deleteError && (
          <div className="person-profile-screen__delete-error" role="alert">
            {deleteError}
            <button
              type="button"
              onClick={() => setDeleteError(null)}
              className="person-profile-screen__delete-error-dismiss"
              aria-label={tForms('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        <div className="profile-hero" data-tour="profile-hero">
          <button
            type="button"
            className="profile-hero__perm"
            onClick={() => setShowRoleExplanation(true)}
            title={tForms('person.permissionTitle')}
          >
            {canDirectModify ? <Unlock width={11} height={11} /> : <Lock width={11} height={11} />}
            <span>{accessPrefix}</span>
            <strong>{getRoleDisplayName(effectiveRole)}</strong>
          </button>

          <div className="profile-hero__photo-wrap">
            <ProfilePhotoUpload
              familyId={familyIdParam!}
              familyMemberId={familyMemberIdParam!}
              canModify={canDirectModify}
              photoUrl={photoUrl}
              photoDisplayStatus={photoDisplayStatus}
              onUploadSuccess={handlePhotoUploadSuccess}
              variant="profile"
              placeholderInitials={profileInitials(personData.givenName, personData.surname)}
            />
          </div>

          <div className="profile-hero__info">
            <h1 className="profile-hero__name">
              {givenAndMiddle}
              {surname && (
                <>
                  {' '}
                  <em>{surname}</em>
                </>
              )}
              {suffix && ` ${suffix}`}
            </h1>

            {(personData.birthDate || personData.birthPlace?.trim()) && (
              <div className="profile-hero__line">
                {personData.birthDate ? (
                  <span>
                    {tCommon('genealogy.bornDate', {
                      date: formatTemporal(
                        personData.birthDate,
                        (personData.birthDatePrecision as DatePrecision) ?? 'day'
                      )
                    })}
                  </span>
                ) : (
                  <span>{tForms('person.born')}</span>
                )}
                {personData.birthPlace?.trim() && (
                  <>
                    <span className="profile-hero__sep">·</span>
                    <em>{personData.birthPlace.trim()}</em>
                  </>
                )}
              </div>
            )}

            {showDeathLines && (personData.deathDate || personData.deathPlace?.trim()) && (
              <div className="profile-hero__line">
                {personData.deathDate ? (
                  <span>
                    {tCommon('genealogy.diedDate', {
                      date: formatTemporal(
                        personData.deathDate,
                        (personData.deathDatePrecision as DatePrecision) ?? 'day'
                      )
                    })}
                  </span>
                ) : (
                  <span>{tForms('person.died')}</span>
                )}
                {personData.deathPlace?.trim() && (
                  <>
                    <span className="profile-hero__sep">·</span>
                    <em>{personData.deathPlace.trim()}</em>
                  </>
                )}
              </div>
            )}

            {lifespanText && <div className="profile-hero__lifespan">{lifespanText}</div>}

            <div className="profile-hero__chips">
              {personData.gender && <span className="profile-chip">{personData.gender}</span>}
              {showPreferredChip && (
                <span className="profile-chip">
                  {t('personProfile.preferredLabel')} <strong>{preferredName}</strong>
                </span>
              )}
              {isDeceased && <span className="profile-chip profile-chip--deceased">{tForms('person.deceased')}</span>}
              {isClaimedByYou && (
                <span className="profile-chip profile-chip--you">{t('personProfile.claimedByYou')}</span>
              )}
            </div>

            <div className="profile-hero__actions">
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleViewTree}>
                <Target width={14} height={14} />
                {tForms('person.viewInTree')}
              </button>
              {canSuggest ? (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  aria-label={tForms('person.suggestChanges')}
                  onClick={() => setShowSuggestModal(true)}
                >
                  {tForms('person.suggestChanges')}
                </button>
              ) : canDirectModify ? (
                <button type="button" className="btn btn--primary btn--sm" onClick={handleEdit}>
                  <Pencil width={14} height={14} />
                  {tForms('person.editProfile')}
                </button>
              ) : null}
              <ProfileMoreMenu
                canDelete={!!canDelete}
                isDeleting={isDeleting}
                showSuggest={canSuggest}
                canManageAccess={canManageAccess}
                onDelete={handleDeleteClick}
                onSuggest={() => setShowSuggestModal(true)}
                onPermissions={() => setShowRoleExplanation(true)}
                onManageAccess={() => setShowManageAccess(true)}
              />
            </div>
          </div>
        </div>

        <div className="profile-sections" id="profile-sections" data-tour="profile-sections">
          <StickyTabStrip
            className="profile-sections__tabs"
            ariaLabel={tForms('person.profileSectionsAria')}
            tabs={[
              { id: 'facts', label: tForms('person.tabs.facts'), count: factsCount },
              { id: 'life-events', label: tForms('person.tabs.lifeEvents'), count: lifeEventsCount },
              { id: 'relationships', label: tForms('person.tabs.relationships'), count: relationshipsCount },
              { id: 'memories', label: tForms('person.tabs.memories'), count: memoriesCount }
            ]}
            activeTab={activeSection}
            onTabChange={setActiveSection}
          />

          <p className="profile-section-lede" id={`panel-${activeSection}-lede`}>
            {t(PROFILE_SECTION_LEDE_KEYS[activeSection])}
          </p>

          <div
            className="profile-section-panel"
            role="tabpanel"
            id={`panel-${activeSection}`}
            aria-labelledby={`tab-${activeSection}`}
            aria-describedby={`panel-${activeSection}-lede`}
          >
            {activeSection === 'facts' && (
              <div data-tour="profile-facts">
                <PersonFacts
                  familyMemberId={familyMemberIdParam!}
                  familyId={familyIdParam ?? undefined}
                  readonly={!canDirectModify}
                  variant="profile"
                  sectionMode="tab-panel"
                />
              </div>
            )}
            {activeSection === 'life-events' && (
              <div data-tour="profile-life-events">
                <PersonLifeEvents
                  familyMemberId={familyMemberIdParam!}
                  familyId={familyIdParam ?? undefined}
                  readonly={!canDirectModify}
                  variant="profile"
                  sectionMode="tab-panel"
                />
              </div>
            )}
            {activeSection === 'relationships' && (
              <div data-tour="profile-relationships">
                <PersonRelationships
                  familyMemberId={familyMemberIdParam!}
                  familyId={familyIdParam ?? undefined}
                  readonly={!canDirectModify}
                  variant="profile"
                  sectionMode="tab-panel"
                />
              </div>
            )}
            {activeSection === 'memories' && (
              <div data-tour="profile-memories">
                <PersonStories
                  familyId={familyIdParam!}
                  familyMemberId={familyMemberIdParam!}
                  firstName={personData.givenName?.trim() || t('personProfile.thisPerson')}
                  readonly={!canDirectModify}
                  effectiveRole={effectiveRole}
                  canModifyMember={canDirectModify}
                  sectionMode="tab-panel"
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <ScreenTourLayer tour={profileTour} />

      {familyMemberIdParam && (
        <RoleExplanationModal
          familyMemberId={familyMemberIdParam}
          isOpen={showRoleExplanation}
          onClose={() => setShowRoleExplanation(false)}
        />
      )}

      {familyIdParam && familyMemberIdParam && (
        <ManageMemberAccessModal
          familyId={familyIdParam}
          familyMemberId={familyMemberIdParam}
          memberLabel={
            personData
              ? `${personData.givenName ?? ''} ${personData.surname ?? ''}`.trim() || t('personProfile.thisPerson')
              : t('personProfile.thisPerson')
          }
          canManage={canManageAccess}
          actorEffectiveRole={effectiveRole}
          isOpen={showManageAccess}
          onClose={() => setShowManageAccess(false)}
        />
      )}

      {personData && familyIdParam && (
        <SuggestChangesModal
          familyId={familyIdParam}
          personData={personData}
          isOpen={showSuggestModal}
          onClose={() => setShowSuggestModal(false)}
          onSuccess={() => {
            setSuggestSubmittedMessage(t('personProfile.suggestSubmitted'));
            if (familyMemberIdParam) {
              void suggestionsStore.loadPendingForMember(familyIdParam, familyMemberIdParam);
            }
            window.setTimeout(() => setSuggestSubmittedMessage(null), 8000);
          }}
          onRequestUpgrade={() => {
            setShowSuggestModal(false);
            setShowUpgradeModal(true);
          }}
        />
      )}

      {showUpgradeModal && familyIdParam && (
        <UpgradeFamilyPlanModal
          familyId={familyIdParam}
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </div>
  );
});

export default PersonProfileScreen;
